import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [currentBranch, setCurrentBranch] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchSettings, setBranchSettings] = useState({
    logo_url: null,
    cor_primaria: "#2563eb",
    cor_secundaria: "#1e40af",
    nomenclaturas: { pessoas: "Pessoas", ministerio: "Ministério", funcao: "Função" }
  });

  // Load settings for current branch
  const loadBranchSettings = useCallback(async (branchId) => {
    if (!branchId) return;
    try {
      const response = await axios.get(`${API}/settings/system`, {
        params: { filial_id: branchId }
      });
      setBranchSettings(response.data);
    } catch (error) {
      console.error("Error loading branch settings:", error);
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        try {
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
          
          // Load user's branches
          try {
            const branchesRes = await axios.get(`${API}/branches/my`);
            setBranches(branchesRes.data || []);
            
            // Try to restore cached branch object first (for immediate display)
            const cachedBranchJson = localStorage.getItem("currentBranch");
            let cachedBranch = null;
            try {
              cachedBranch = cachedBranchJson ? JSON.parse(cachedBranchJson) : null;
              // Validate cached branch has required fields
              if (cachedBranch && (!cachedBranch.id || !cachedBranch.nome)) {
                console.log("Invalid cached branch, clearing...");
                cachedBranch = null;
                localStorage.removeItem("currentBranch");
              }
            } catch (e) {
              // Invalid JSON (might be old format with just ID string)
              console.log("Clearing invalid cached branch data");
              cachedBranch = null;
              localStorage.removeItem("currentBranch");
            }
            
            // Set current branch from localStorage or default to first branch
            const savedBranchId = localStorage.getItem("currentBranchId");
            let selectedBranch = null;
            
            if (savedBranchId && (branchesRes.data || []).find(b => b.id === savedBranchId)) {
              // Found in API response - use fresh data from API (most reliable)
              selectedBranch = branchesRes.data.find(b => b.id === savedBranchId);
            } else if (cachedBranch && cachedBranch.id && cachedBranch.nome) {
              // Use cached branch if it has required fields and API didn't return it
              selectedBranch = cachedBranch;
            } else if (branchesRes.data && branchesRes.data.length > 0) {
              // Default to first branch
              selectedBranch = branchesRes.data[0];
            }
            
            // Always save both ID and full object to localStorage
            if (selectedBranch && selectedBranch.id && selectedBranch.nome) {
              localStorage.setItem("currentBranchId", selectedBranch.id);
              localStorage.setItem("currentBranch", JSON.stringify(selectedBranch));
            }
            
            setCurrentBranch(selectedBranch);
            
            // Load branch settings
            if (selectedBranch) {
              await loadBranchSettings(selectedBranch.id);
            }
          } catch (branchError) {
            console.error("Error loading branches:", branchError);
            // Continue without branches - don't logout
          }
        } catch (error) {
          console.error("Auth error:", error);
          logout();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [token, loadBranchSettings]);

  const login = async (email, senha) => {
    const response = await axios.post(`${API}/auth/login`, { email, senha });
    const { token: newToken, user: userData } = response.data;
    
    localStorage.setItem("token", newToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    
    // Load branches after login
    try {
      const branchesRes = await axios.get(`${API}/branches/my`);
      setBranches(branchesRes.data);
      if (branchesRes.data.length > 0) {
        const savedBranchId = localStorage.getItem("currentBranchId");
        let selectedBranch = null;
        if (savedBranchId && branchesRes.data.find(b => b.id === savedBranchId)) {
          selectedBranch = branchesRes.data.find(b => b.id === savedBranchId);
        } else {
          selectedBranch = branchesRes.data[0];
        }
        setCurrentBranch(selectedBranch);
        localStorage.setItem("currentBranchId", selectedBranch.id);
        localStorage.setItem("currentBranch", JSON.stringify(selectedBranch));
      }
    } catch (e) {
      console.error("Error loading branches:", e);
    }
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("currentBranchId");
    localStorage.removeItem("currentBranch");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    setCurrentBranch(null);
    setBranches([]);
  };

  const switchBranch = async (branch) => {
    setCurrentBranch(branch);
    localStorage.setItem("currentBranchId", branch.id);
    localStorage.setItem("currentBranch", JSON.stringify(branch));
    // Load settings for the new branch
    await loadBranchSettings(branch.id);
  };

  const acceptTerms = () => {
    // Update local user state after accepting terms
    setUser(prev => ({
      ...prev,
      termos_aceitos: true,
      termos_aceitos_em: new Date().toISOString()
    }));
  };

  const needsTermsAcceptance = () => {
    return user && !user.termos_aceitos;
  };

  const isAdmin = () => user?.nivel_acesso === "admin";
  const isVisitor = () => user?.nivel_acesso === "visitante";

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      logout, 
      isAdmin, 
      isVisitor,
      currentBranch,
      branches,
      switchBranch,
      branchSettings,
      loadBranchSettings,
      acceptTerms,
      needsTermsAcceptance
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
